import { MouseEvent } from "react";
import { RiArrowUpSLine, RiPushpin2Fill, RiPushpin2Line } from "react-icons/ri";

import { cn } from "~/utils/fontsConfig";
import { RouterOutputs } from "~/utils/api";
import { checkIfItemIsPinned } from "~/server/api/helpers/pinnedItemsHelpers";

import { SidebarItem } from "../community-components/CommunitySideBar/SidebarTypes";
import {
    usePinnedItemsMutation,
    usePinnedItemsQuery,
} from "../community-components/_queries/usePinnedItems";
import { SubNavLinkWrapper } from "./SubNavLinkWrapper";

type Props = {
    community?: RouterOutputs["community"]["getSimpleCommunityBySlug"];
    sideBarItem: SidebarItem;
    depth: number;
    state: Map<string, boolean>;
    toggle: (path: string) => void;
};

export const SubNavigationItem = ({
    community,
    sideBarItem,
    depth,
    state,
    toggle,
}: Props) => {
    const path = `${depth}-${sideBarItem.name}`;
    const isOpen = state.has(path) ? state.get(path) : state.set(path, false);

    const { data: pinnedItems } = usePinnedItemsQuery(community?.id);
    const pinnedItemsMutation = usePinnedItemsMutation(community?.id as string);

    const isPinned = checkIfItemIsPinned(pinnedItems ?? [], [sideBarItem.name]);

    const toggleChildren = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        toggle(path);
    };

    const pinItem = (
        e: MouseEvent<HTMLDivElement>,
        sideBarItem: SidebarItem,
    ) => {
        e.stopPropagation();
        e.preventDefault();

        if (!community) return;

        let updatedPinnedItems: string[] = [];

        if (isPinned && !!pinnedItems) {
            updatedPinnedItems = pinnedItems.filter(
                (item: string) => item !== sideBarItem.name,
            );
        } else {
            updatedPinnedItems = [...(pinnedItems ?? []), sideBarItem.name];
        }

        pinnedItemsMutation.mutate(updatedPinnedItems);
    };

    return (
        <div>
            <SubNavLinkWrapper
                sideBarItem={sideBarItem}
                depth={depth + 1}
                state={state}
            >
                <div className="col-start-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center justify-between gap-1">
                            {sideBarItem.Icon && (
                                <sideBarItem.Icon className="h-5 w-5" />
                            )}
                            <p>{sideBarItem.name}</p>
                        </div>

                        {/* we only allow level 1 subnav items to be pinned */}
                        {community && depth === 1 && (
                            <div
                                className="cursor-pointer p-2 transition-colors duration-300 ease-in-out"
                                onClick={(e) => pinItem(e, sideBarItem)}
                            >
                                {isPinned ? (
                                    <RiPushpin2Fill className="text-xl text-gray-500" />
                                ) : (
                                    <RiPushpin2Line className="text-xl text-gray-500" />
                                )}
                            </div>
                        )}

                        {sideBarItem.items?.length && (
                            <div
                                className={cn(
                                    "transform cursor-pointer p-2 transition-transform duration-300 ease-in-out",
                                    {
                                        "rotate-180": isOpen,
                                        "rotate-0": !isOpen,
                                    },
                                )}
                                onClick={(e) => toggleChildren(e)}
                            >
                                <RiArrowUpSLine className="text-xl" />
                            </div>
                        )}
                    </div>
                </div>
            </SubNavLinkWrapper>

            {/* Recursive call for child items */}
            <div
                className={cn(
                    "duration-600 overflow-hidden transition-[max-height] ease-in-out",
                    {
                        "max-h-0": !isOpen,
                        "max-h-[1000px]": isOpen,
                    },
                )}
                style={{ paddingLeft: depth * 10 }}
            >
                {sideBarItem.items?.map((item) => {
                    return (
                        <SubNavigationItem
                            community={community}
                            sideBarItem={item}
                            depth={depth + 1}
                            state={state}
                            toggle={toggle}
                        ></SubNavigationItem>
                    );
                })}
            </div>
        </div>
    );
};
